import { Flex } from 'antd';
import CardData from '../dashboard/cardData';
import { Button, CardCta, Divider, Paragraph } from '../Ant';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { useTranslation } from 'react-i18next';
import {
  AddSquare,
  ArrowRight,
  Crown,
  Dashboard,
  DollarCircle,
  LanguageFilled,
  Receive,
  ShieldTick,
  User,
  Verify,
} from '../icons';
import { IMatterRequest } from '../../interface/matterRequest';
import dayjs from 'dayjs';
import { getTranslationFromTranslatableEntity, MatterRequestStatus } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { displayLanguageName, getLocalDateAndTime } from '../../constants/utilityFunctions';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { cardBorderColorMapping, tagColorMapping } from '../../constants/matterRequest';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useLoading } from '../../context/LoadingContext';
import NoteIcons from '../common/NoteIcons';
import { useRestrictedUserMatterPermissions } from '../../hooks/useRestrictedUserMatterPermissions';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const MatterRequestGeneralCard = ({
  matterRequest,
  isLoading = false,
  refetchMatterRequest,
}: {
  matterRequest: IMatterRequest;
  isLoading?: boolean;
  refetchMatterRequest?: () => Promise<IMatterRequest | undefined>;
}) => {
  const { t } = useTranslation();
  const { canEditMatterRequests } = useRestrictedUserMatterPermissions();
  const { getAppLanguageCode } = useLanguage();
  const { push } = useNavigationStack();
  const { updateManageMatterRequest } = useManageMatterRequestContext();
  const { setIsLoading } = useLoading();
  const handleGoToCustomer = () => {
    push(
      ROUTES.CUSTOMER_LISTING +
        `/${matterRequest?.principalCompany?.id ?? matterRequest?.principalPrivatePerson?.id}`,
    );
  };
  const fetchLatestMatterRequest = async () => {
    setIsLoading(true);
    try {
      return (await refetchMatterRequest?.()) ?? matterRequest;
    } finally {
      setIsLoading(false);
    }
  };
  const handleEditMatterRequest = async () => {
    const latestMatterRequest = await fetchLatestMatterRequest();
    updateManageMatterRequest(latestMatterRequest, true);
    push(`${ROUTES.MANAGE_MATTER_REQUEST}/${latestMatterRequest.id}`);
  };
  if (isLoading) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 1, 1, 2, 1]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  return (
    <PrimaryCard
      data-testid="matter-request-general-card"
      $isDetail
      className={`w-full conflict-card d-flex flex-col h-full ${cardBorderColorMapping[matterRequest?.status]}`}
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={10} align="center">
          <Flex
            data-testid="matter-request-general-card-status-chip"
            justify="center"
            align="center"
            className={`status-chip ${tagColorMapping[matterRequest?.status]}`}
          >
            {t(`matterRequest:status.${matterRequest?.status}`)}
          </Flex>
          <NoteIcons
            hasImportantNotes={matterRequest?.hasImportantNotes}
            hasNotes={matterRequest?.hasNotes}
          />
        </Flex>

        <Paragraph
          data-testid="matter-request-general-card-created-on"
          $fontsize="13px"
          $weight={400}
          $color="title"
          $lineheight="20px"
        >
          {getLocalDateAndTime(new Date(matterRequest?.createdOn), true)}
        </Paragraph>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10} className="flex-grow">
        <CardData
          type="matter"
          icon={<Receive />}
          title={t('matterRequest:matterRequest')}
          content={
            matterRequest?.principalCompany?.name ??
            matterRequest?.principalPrivatePerson?.fullName ??
            '-'
          }
          subcontent={matterRequest?.subject ?? '-'}
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-1"
          headingcontent={
            <Button
              type="text"
              data-testid="matter-request-general-card-view-customer"
              icon={<ArrowRight />}
              iconPosition="end"
              className="text-cta"
              onClick={handleGoToCustomer}
            >
              {getViewLabel(t, 'customer:customer')}
            </Button>
          }
        />
        <CardData
          icon={<ShieldTick />}
          type="transaction"
          title={t('matterRequest:responsiblePerson')}
          content={matterRequest?.responsibleUser?.fullName ?? '-'}
        />
        <Divider />
        <CardData
          icon={<User />}
          type="owner"
          title={t('matterRequest:owner')}
          content={matterRequest?.ownerUser?.fullName ?? '-'}
        />
        <CardData
          icon={<Crown />}
          type="business-area"
          title={t('matter:businessArea')}
          content={
            matterRequest?.businessArea
              ? (getTranslationFromTranslatableEntity(
                  matterRequest.businessArea.translations,
                  getAppLanguageCode(),
                ) ?? matterRequest.businessArea.name)
              : '-'
          }
        />
        <CardData
          icon={<Dashboard />}
          type="matter-request-type"
          title={t('matterRequest:type')}
          content={
            getTranslationFromTranslatableEntity(
              matterRequest?.matterRequestType?.translations,
              getAppLanguageCode(),
            ) ?? matterRequest.matterRequestType?.name
          }
        />
        <Divider />
        <Flex justify="space-between">
          <CardData
            icon={<LanguageFilled />}
            type="language"
            title={t('common:language')}
            content={
              matterRequest?.language
                ? displayLanguageName(matterRequest?.language, getAppLanguageCode())
                : '-'
            }
          />
          <CardData
            icon={<DollarCircle />}
            type="matter-request-currency"
            title={t('matterRequest:currency')}
            content={matterRequest?.currency ?? '-'}
          />
        </Flex>
        <Divider />
        <CardData
          icon={<Verify />}
          type="conflict-check-done-date"
          title={t('matterRequest:conflictCheckDoneDate')}
          content={
            matterRequest?.checkedOnDate
              ? dayjs(matterRequest?.checkedOnDate).format('MMM DD, YYYY')
              : '-'
          }
        />
      </Flex>
      {matterRequest.status !== MatterRequestStatus.rejected &&
        matterRequest.status !== MatterRequestStatus.closed &&
        canEditMatterRequests && (
          <Flex vertical gap={10} className="w-full">
            <Divider />
            <CardCta data-testid="create-preliminary-invoice" onClick={handleEditMatterRequest}>
              <AddSquare />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="15px">
                {t('matterRequest:editMatterRequest')}
              </Paragraph>
            </CardCta>
          </Flex>
        )}
    </PrimaryCard>
  );
};

export default MatterRequestGeneralCard;
